<template>
  <div class="vap-page">
    <Loading
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div class="vap-page-header">
      <h1>Amazon Rekognition Management</h1>
      <div class="btns-container">
        <VasionButton classProp="secondary" @vasionButtonClicked="cancel()">
          Cancel
        </VasionButton>
        <VasionButton
          classProp="primary"
          :isDisabled="!(isDirty && formReadyToBeSaved)"
          @vasionButtonClicked="save()"
        >
          Save
        </VasionButton>
      </div>
    </div>
    <div class="vap-page-content">
      <RekognitionForm
        ref="rekognitionForm"
        :rekognitionDataToEdit="rekognitionDataToEdit"
        @markAsDirty="markAsDirty"
        @markFormStatus="markFormStatus"
        @markLoadingStatus="markLoadingStatus"
      />
    </div>
    <md-dialog
      id="modal-leave-confirmation"
      :md-active.sync="showLeaveDialog"
      :md-click-outside-to-close="false"
    >
      <VasionConfirmationDialog
        message="Are you sure you want to leave? Changes have not been saved."
        @noButtonClick="cancelLeave()"
        @yesButtonClick="doLeave()"
      />
    </md-dialog>
    <VasionSnackbar
      id="snackbar-rekognition-management"
      ref="rekognitionManagementSnackbar"
      :showSnackbarBool.sync="snackbar.showSnackbar"
      :snackbarImage="snackbar.snackbarImage"
      :snackbarSubTitle="snackbar.snackbarSubTitle"
      :snackbarTitle="snackbar.snackbarTitle"
    />
  </div>
</template>
<script>
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import Loading from 'vue-loading-overlay'
import RekognitionForm from '@/components/capture/RekognitionForm.vue'

export default {
  name: 'RekognitionManagement',
  components: {
    Loading,
    RekognitionForm,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      this.routeTo = to
      this.showLeaveDialog = true
    } else {
      this.routeTo = null
      next()
    }
  },
  data() {
    return {
      formReadyToBeSaved: false,
      isDirty: false,
      isLoading: false,
      rekognitionId: this.$route.params.rekognitionId,
      loaderBackgroundColor,
      loaderColor,
      rekognitionDataToEdit: {},
      showLeaveDialog: false,
      snackbar: {
        showSnackbar: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
      },
    }
  },
  computed: {
    isEdit() {
      return this.rekognitionId ? true : false
    },
    isTextracLicenseActivated() {
      return this.$store.state.common.isTextractLicensed
    },
  },
  created: async function () {
    if (this.isTextracLicenseActivated === null) {
      await this.$store.dispatch('common/getIsTextractLicensed')
    }

    if (!this.isTextracLicenseActivated) {
      this.$router.push({ name: 'AdvancedImageProcessing' })
    }

    if (this.isEdit && this.rekognitionId)
      await this.getSingleRegistry(this.rekognitionId)
  },
  methods: {
    cancel() {
      if (this.isDirty) {
        this.routeTo = null
        this.showLeaveDialog = true
      } else {
        this.$router.push({ name: 'AdvancedImageProcessing' })
      }
    },
    cancelLeave() {
      this.showLeaveDialog = false
    },
    doLeave() {
      this.isDirty = false
      this.showLeaveDialog = false
      if (this.routeTo) {
        this.$router.push({ path: this.routeTo.path })
      } else {
        this.$router.push({ name: 'AdvancedImageProcessing' })
      }
    },
    async getSingleRegistry(id) {
      this.isLoading = true

      try {
        const response = await this.$store.dispatch(
          'admin/getRekognitionAIP',
          id
        )
        if (response?.Data) {
          this.rekognitionDataToEdit = response.Data
        } else if (response?.Value === 'False') {
          this.showSnackbar(
            'Amazon Rekognition AIP',
            response.ResultMessage || 'An error occurred while fetching data',
            false
          )
        } else {
          this.showSnackbar(
            'Amazon Rekognition AIP',
            'An error occurred while fetching data',
            false
          )
          setTimeout(() => {
            this.$router.push({ name: 'AdvancedImageProcessing' })
          }, 1500)
        }
      } catch (error) {
        this.showSnackbar(
          'Amazon Rekognition AIP',
          'An unexpected error occurred',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    markAsDirty() {
      this.isDirty = true
    },
    markFormStatus(status) {
      this.formReadyToBeSaved = status
    },
    markLoadingStatus(status) {
      this.isLoading = status
    },
    async save() {
      if (!this.formReadyToBeSaved) return

      this.isLoading = true
      const {
        name,
        awsCollection,
        endpoint,
        awsRegion,
        awsBucket,
        awsAccessKeyID,
        awsSecretKey,
        object,
        similarityThreshold,
        fieldId,
      } = this.$refs.rekognitionForm.formFields

      let payload = {
        Id: this.isEdit ? this.rekognitionId : 0,
        Name: name,
        RekognitionCollection: awsCollection?.value,
        RekognitionEndpoint: endpoint?.value,
        AwsConfig: {
          Region: awsRegion?.value,
          BucketName: awsBucket?.value,
          ClientID: awsAccessKeyID,
          ClientSecret: awsSecretKey,
        },
        IndexFormId: object?.value || 0,
        MinimumSimilarityThreshold: similarityThreshold,
        FieldId: fieldId?.id,
      }

      try {
        const response = await this.$store.dispatch(
          'admin/saveRekognitionAIP',
          payload
        )
        const success = response && response.Value === 'True'
        this.showSnackbar(
          'Amazon Rekognition AIP',
          success
            ? 'Amazon Rekognition AIP Successfully Saved'
            : response?.ResultMessage || 'An error occurred during save',
          success
        )
        if (success) {
          this.isDirty = false
          setTimeout(() => {
            this.$router.push({ name: 'AdvancedImageProcessing' })
          }, 2000)
        }
      } catch (error) {
        this.showSnackbar(
          'Amazon Rekognition AIP',
          'An unexpected error occurred',
          false
        )
      } finally {
        this.isLoading = false
      }
    },
    showSnackbar(title, subTitle, isSuccess) {
      this.snackbar = {
        snackbarTitle: title,
        snackbarSubTitle: subTitle,
        snackbarImage: isSuccess,
        showSnackbar: true,
      }
    },
  },
}
</script>
